/**
 * Route Guard Components for React Router 6
 *
 * These components replace the onEnter hooks from React Router 3.
 * They wrap route elements and handle authentication/authorization redirects.
 */

import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'

import {
  isLoggedInSelector,
  canGiftSubscriptionSelector
} from 'reduxModules/auth/selectors'
import { readyToReviewOrderSelector } from 'reduxModules/checkout/selectors'
import { clientTokenSelector } from 'reduxModules/forms/paymentMethod/selectors'
import {
  isSubscribedReselector,
  isLibrarySubscriberSelector
} from 'reduxModules/users/selectors'

/**
 * Requires user to be authenticated
 * Redirects to /login if not authenticated
 */
export const RequireAuth = ({ children }) => {
  const isLoggedIn = useSelector(isLoggedInSelector)
  const location = useLocation()

  if (!isLoggedIn) {
    return <Navigate to="/login" state={{ next: location.pathname }} replace />
  }

  return children
}

RequireAuth.propTypes = {
  children: PropTypes.node.isRequired
}

/**
 * Requires user to NOT be authenticated
 * Redirects to / if authenticated
 */
export const RequireNoAuth = ({ children }) => {
  const isLoggedIn = useSelector(isLoggedInSelector)
  const location = useLocation()

  if (isLoggedIn) {
    return <Navigate to="/" state={{ next: location.pathname }} replace />
  }

  return children
}

RequireNoAuth.propTypes = {
  children: PropTypes.node.isRequired
}

/**
 * Requires user to have an active subscription
 * Redirects to /subscriptions/plans if not subscribed
 */
export const RequireSubscribed = ({ children }) => {
  const { isSubscribed } = useSelector(isSubscribedReselector)
  const isLoggedIn = useSelector(isLoggedInSelector)
  const location = useLocation()

  if (!isSubscribed || !isLoggedIn) {
    return (
      <Navigate
        to="/subscriptions/plans"
        state={{ next: location.pathname }}
        replace
      />
    )
  }

  return children
}

RequireSubscribed.propTypes = {
  children: PropTypes.node.isRequired
}

/**
 * Requires user to NOT have an active subscription
 * Redirects to /change_subscription if subscribed
 */
export const RequireNotSubscribed = ({ children }) => {
  const { isSubscribed } = useSelector(isSubscribedReselector)
  const location = useLocation()

  if (isSubscribed) {
    return (
      <Navigate
        to="/change_subscription"
        state={{ next: location.pathname }}
        replace
      />
    )
  }

  return children
}

RequireNotSubscribed.propTypes = {
  children: PropTypes.node.isRequired
}

/**
 * Requires order to be ready for review
 * Redirects to /checkout if order not ready
 */
export const RequireOrderReady = ({ children }) => {
  const orderReady = useSelector(readyToReviewOrderSelector)
  const hasClientToken = useSelector(clientTokenSelector) !== ''
  const location = useLocation()

  if (!orderReady || !hasClientToken) {
    return (
      <Navigate to="/checkout" state={{ next: location.pathname }} replace />
    )
  }

  return children
}

RequireOrderReady.propTypes = {
  children: PropTypes.node.isRequired
}

/**
 * Requires user to NOT be a library subscriber
 * Redirects to / if user is a library subscriber
 */
export const RequireNotLibrarySubscriber = ({ children }) => {
  const isLibrarySubscriber = useSelector(isLibrarySubscriberSelector)
  const location = useLocation()

  if (isLibrarySubscriber) {
    return <Navigate to="/" state={{ next: location.pathname }} replace />
  }

  return children
}

RequireNotLibrarySubscriber.propTypes = {
  children: PropTypes.node.isRequired
}

/**
 * Requires user to have gift subscription capability
 * Redirects to /account if user cannot gift subscription
 */
export const RequireGiftSubscription = ({ children }) => {
  const canGiftSubscription = useSelector(canGiftSubscriptionSelector)
  const location = useLocation()

  if (!canGiftSubscription) {
    return (
      <Navigate to="/account" state={{ next: location.pathname }} replace />
    )
  }

  return children
}

RequireGiftSubscription.propTypes = {
  children: PropTypes.node.isRequired
}

export default {
  RequireAuth,
  RequireNoAuth,
  RequireSubscribed,
  RequireNotSubscribed,
  RequireOrderReady,
  RequireNotLibrarySubscriber,
  RequireGiftSubscription
}
